import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, CreditCard, Download, FileText, Flag, HelpCircle, Info, Loader2, Search, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';

import apiService from '../services/api';
import CustomAlertModal from './CustomAlertModal';
import { formatDateOnly, parseDateOnlyToLocalDate, parseDateOrDateTime } from '../utils/dateOnly';
import { getInvoiceAmountDue } from '../utils/invoiceAmountDue';

type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'VOIDED' | string;

type InvoiceRow = {
  id: string;
  business_id: string;
  appointment_id?: string | null;
  bill_to: string;
  customer_email?: string | null;
  invoice_date: string;
  due_date: string;
  status: InvoiceStatus;
  notes?: string | null;
  services: any;
  sub_total: string | number;
  discount: string | number;
  tax: string | number;
  tip?: string | number;
  total: string | number;
  deposit?: string | number;
  due?: string | number;
  created_at?: string;
  updated_at?: string;
  sent_at?: string | null;
  last_resent_at?: string | null;
  paid_at?: string | null;
  appointment_scheduled_at?: string | null;
  amount_due?: number;
  business?: any;
  onlinePaymentsEnabled?: boolean;
};

const safeNum = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (v: any): string => safeNum(v).toFixed(2);

const toInvoiceLabel = (id: string): string => {
  const short = (id || '').split('-')[0]?.toUpperCase() || '—';
  return `INV-${short}`;
};

const fmtDate = (v: any): string => formatDateOnly(v, 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const fmtDateTime = (v: any): string => {
  const d = v instanceof Date ? v : new Date(String(v));
  if (!Number.isFinite(d.getTime())) return '—';
  const date = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`;
};

const daysBetween = (from: any, to: any): number => {
  const a = parseDateOrDateTime(from);
  const b = parseDateOrDateTime(to);
  if (!a || !b) return 0;
  const diff = Math.floor((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
  return diff;
};

const startOfLocalDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** Calendar days from today to due date (0 = due today, negative = past due). */
const dueDateOffsetFromToday = (dueDateValue: any, today = new Date()): number | null => {
  const due = parseDateOnlyToLocalDate(dueDateValue);
  if (!due) return null;
  const todayStart = startOfLocalDay(today);
  const dueStart = startOfLocalDay(due);
  return Math.floor((dueStart.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
};


const fmtExportDate = (v: any): string =>
  formatDateOnly(v, 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });

const fmtExportDateTime = (v: any): string => {
  if (v == null || v === '') return '—';
  const d = new Date(String(v));
  if (!Number.isFinite(d.getTime())) return '—';
  const datePart = d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const timePart = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart} @ ${timePart}`;
};

const escapeCsvCell = (value: unknown): string => {
  const s = String(value ?? '');
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const formatStatusForExport = (status: InvoiceStatus): string => {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID') return 'Paid';
  if (s === 'SENT') return 'Unpaid';
  return s || '—';
};

const buildInvoiceHistoryCsv = (invoices: InvoiceRow[]): string => {
  const headers = [
    'Invoice Number',
    'Business',
    'Status',
    'Invoice Date',
    'Due Date',
    'Amount Due',
    'Total',
    'Invoice Sent',
    'Invoice Resent',
    'Invoice Paid',
    'From Appointment',
  ];
  const lines = invoices.map((r) =>
    [
      toInvoiceLabel(r.id),
      getBusinessName(r),
      formatStatusForExport(r.status),
      fmtExportDate(r.invoice_date),
      fmtExportDate(r.due_date),
      formatMoney(getInvoiceAmountDue(r)),
      formatMoney(r.total),
      fmtExportDate(r.sent_at),
      fmtExportDate(r.last_resent_at),
      fmtExportDate(r.paid_at),
      r.appointment_id ? fmtExportDateTime(r.appointment_scheduled_at) : '—',
    ]
      .map(escapeCsvCell)
      .join(',')
  );
  return [headers.map(escapeCsvCell).join(','), ...lines].join('\r\n');
};

const downloadCsvFile = (csv: string, filename: string) => {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const parseServices = (raw: any): Array<{ description: string; qty: number; amount: number; unitPrice?: number; professionalName?: string | null }> => {
  let arr: any[] = [];
  try {
    if (Array.isArray(raw)) arr = raw;
    else if (typeof raw === 'string') arr = JSON.parse(raw || '[]');
    else if (raw && typeof raw === 'object') arr = raw;
  } catch {
    arr = [];
  }

  return (Array.isArray(arr) ? arr : []).map((s) => {
    const qty = Math.max(0, safeNum(s?.qty ?? 0));
    const unitPrice = Math.max(0, safeNum(s?.unitPrice ?? 0));
    const included = s?.included !== false; // default true
    const amount = included ? Math.max(0, safeNum(s?.amount ?? qty * unitPrice)) : 0;
    return {
      description: String(s?.description ?? '').trim() || 'Item',
      qty,
      unitPrice,
      amount,
      professionalName: s?.professionalName ?? null,
    };
  });
};

const getServiceStartDateTime = (raw: any): Date | null => {
  let arr: any[] = [];
  try {
    if (Array.isArray(raw)) arr = raw;
    else if (typeof raw === 'string') arr = JSON.parse(raw || '[]');
    else if (raw && typeof raw === 'object') arr = raw;
  } catch {
    arr = [];
  }

  const candidates: Date[] = [];
  for (const s of Array.isArray(arr) ? arr : []) {
    // Prefer the booking time captured on the line item.
    const v = s?.fromDateTime ?? s?.from_date_time ?? s?.from_datetime ?? s?.from ?? s?.scheduledAt ?? s?.scheduled_at ?? null;
    if (!v) continue;
    const d = new Date(String(v));
    if (!Number.isFinite(d.getTime())) continue;
    candidates.push(d);
  }
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.getTime() - b.getTime());
  return candidates[0];
};

const getBusinessName = (row: InvoiceRow): string => {
  const b: any = (row as any)?.business;
  const name = String(b?.name ?? b?.business_name ?? '').trim();
  return name || 'Business';
};

const getBusinessShortName = (row: InvoiceRow): string => {
  const b: any = (row as any)?.business;
  const shortName = String(b?.shortName ?? b?.short_name ?? b?.business_short_name ?? b?.businessShortName ?? '').trim();
  return shortName;
};

const getBusinessAddressLine = (row: InvoiceRow): string => {
  const b: any = (row as any)?.business;
  const address = String(b?.address ?? '').trim();
  const city = String(b?.city ?? '').trim();
  const state = String(b?.state ?? '').trim();
  const zip = String(b?.zip_code ?? b?.zipCode ?? b?.zip ?? '').trim();
  const line2 = [city, state, zip].filter(Boolean).join(', ').replace(/,\s*,/g, ', ').trim();
  return [address, line2].filter(Boolean).join(', ').trim();
};

const getBusinessEmail = (row: InvoiceRow): string => {
  const b: any = (row as any)?.business;
  const email = String(b?.email ?? '').trim();
  return email;
};

const getBusinessPhone = (row: InvoiceRow): string => {
  const b: any = (row as any)?.business;
  const phone = String(b?.phone ?? b?.phone_number ?? b?.phoneNumber ?? '').trim();
  return phone;
};

const statusBadge = (status: InvoiceStatus) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID') return { label: 'Paid', className: 'bg-green-50 text-green-700 border-green-200' };
  if (s === 'SENT') return { label: 'Unpaid', className: 'bg-amber-50 text-amber-800 border-amber-200' };
  return { label: s || '—', className: 'bg-gray-50 text-gray-700 border-gray-200' };
};

const pillClass = (active: boolean) =>
  active
    ? 'px-4 py-2 rounded-full text-sm font-medium text-white transition-colors bg-[#0B4778] border border-[#0B4778]'
    : 'px-4 py-2 rounded-full text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors';

const CustomerInvoicesSection: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [metricRows, setMetricRows] = useState<InvoiceRow[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'ALL' | 'SENT' | 'PAID'>('ALL');
  const [businessId, setBusinessId] = useState<'ALL' | string>('ALL');

  const [limit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [paginationTotal, setPaginationTotal] = useState(0);

  const [howOpen, setHowOpen] = useState(false);
  const [reportInfoOpen, setReportInfoOpen] = useState(false);
  const [downloadingHistory, setDownloadingHistory] = useState(false);

  const businesses = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const r of rows) {
      if (!r.business_id) continue;
      if (!map.has(r.business_id)) {
        map.set(r.business_id, { id: r.business_id, name: getBusinessName(r) });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let x = rows;
    if (status !== 'ALL') {
      x = x.filter((r) => String(r.status || '').toUpperCase() === status);
    }
    if (businessId !== 'ALL') {
      x = x.filter((r) => r.business_id === businessId);
    }
    // Search is server-side; keep a tiny client-side guard for immediate UX.
    const q = search.trim().toLowerCase();
    if (q) {
      x = x.filter((r) => {
        const hay = [
          toInvoiceLabel(r.id),
          r.id,
          getBusinessName(r),
          String(r.notes ?? ''),
          String(r.bill_to ?? ''),
          String(r.customer_email ?? ''),
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return x;
  }, [rows, status, businessId, search]);

  const metrics = useMemo(() => {
    const outstanding = metricRows.filter((r) => String(r.status || '').toUpperCase() === 'SENT');
    const outstandingTotal = outstanding.reduce((sum, r) => sum + getInvoiceAmountDue(r), 0);

    const dueSoonInvoices = outstanding.filter((r) => {
      const offset = dueDateOffsetFromToday(r.due_date);
      return offset != null && offset >= 0 && offset <= 7;
    });
    const dueSoonTotal = dueSoonInvoices.reduce((sum, r) => sum + getInvoiceAmountDue(r), 0);

    const pastDueInvoices = outstanding.filter((r) => {
      const offset = dueDateOffsetFromToday(r.due_date);
      return offset != null && offset < 0;
    });
    const pastDueTotal = pastDueInvoices.reduce((sum, r) => sum + getInvoiceAmountDue(r), 0);

    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    const paidLast30 = metricRows
      .filter((r) => String(r.status || '').toUpperCase() === 'PAID')
      .filter((r) => {
        const t = parseDateOrDateTime(r.updated_at || r.invoice_date || r.created_at || '');
        return t ? t >= thirtyDaysAgo : false;
      });
    const paidLast30Total = paidLast30.reduce((sum, r) => sum + safeNum(r.total), 0);

    return {
      outstandingTotal,
      outstandingCount: outstanding.length,
      dueSoonTotal,
      pastDueTotal,
      pastDueCount: pastDueInvoices.length,
      paidLast30Total,
      paidLast30Count: paidLast30.length,
    };
  }, [metricRows]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const sharedFilters = {
        businessId: businessId === 'ALL' ? undefined : businessId,
        search: search.trim() ? search.trim() : undefined,
      };
      const [resp, metricsResp] = await Promise.all([
        apiService.getMyInvoices({
          ...sharedFilters,
          status: status === 'ALL' ? undefined : status,
          limit,
          offset,
        }),
        apiService.getMyInvoices({
          ...sharedFilters,
          limit: 100,
          offset: 0,
        }),
      ]);
      setRows(resp.data as any);
      setMetricRows(metricsResp.data as any);
      setPaginationTotal(safeNum(resp.pagination?.total));
    } catch (e: any) {
      console.error('Failed to load customer invoices', e);
      toast.error(e?.response?.data?.error || 'Failed to load invoices');
      setRows([]);
      setMetricRows([]);
      setPaginationTotal(0);
    } finally {
      setLoading(false);
    }
  }, [businessId, limit, offset, search, status]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownloadPdf = useCallback(async (invoiceId: string) => {
    try {
      const { blob, filename } = await apiService.downloadInvoicePdf(invoiceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `${toInvoiceLabel(invoiceId)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error('PDF download failed', e);
      toast.error(e?.response?.data?.error || 'Failed to download PDF');
    }
  }, []);

  const handleDownloadHistory = useCallback(async () => {
    setDownloadingHistory(true);
    const toastId = toast.loading('Preparing invoice history…');
    try {
      const sharedFilters = {
        businessId: businessId === 'ALL' ? undefined : businessId,
        search: search.trim() ? search.trim() : undefined,
        status: status === 'ALL' ? undefined : status,
      };
      const pageSize = 100;
      let offsetCursor = 0;
      let total = Infinity;
      const allRows: InvoiceRow[] = [];

      while (offsetCursor < total) {
        const resp = await apiService.getMyInvoices({
          ...sharedFilters,
          limit: pageSize,
          offset: offsetCursor,
        });
        const batch = (resp.data ?? []) as InvoiceRow[];
        allRows.push(...batch);
        total = safeNum(resp.pagination?.total) || allRows.length;
        offsetCursor += pageSize;
        if (batch.length === 0) break;
      }

      if (allRows.length === 0) {
        toast.error('No invoices to export for the current filters', { id: toastId });
        return;
      }

      const csv = buildInvoiceHistoryCsv(allRows);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsvFile(csv, `invoice-history-${stamp}.csv`);
      toast.success(`Downloaded ${allRows.length} invoice${allRows.length === 1 ? '' : 's'}`, { id: toastId });
    } catch (e: any) {
      console.error('Failed to download invoice history', e);
      toast.error(e?.response?.data?.error || 'Failed to download invoice history', { id: toastId });
    } finally {
      setDownloadingHistory(false);
    }
  }, [businessId, search, status]);

  const pageInfo = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil((paginationTotal || 0) / limit));
    const currentPage = Math.floor(offset / limit) + 1;
    return { totalPages, currentPage };
  }, [paginationTotal, limit, offset]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold mb-0">My Invoices</h1>
          <p className="text-gray-600 text-sm mb-0">
            This page shows invoices that a business has sent you to pay later (not every appointment has an invoice).
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:flex-nowrap sm:w-auto">
          <button
            type="button"
            onClick={() => setHowOpen(true)}
            className="aacore-btn-outlined w-full sm:w-auto justify-center"
          >
            <HelpCircle className="w-4 h-4" />
            How invoices work
          </button>
          <button
            type="button"
            onClick={handleDownloadHistory}
            disabled={downloadingHistory}
            className="aacore-btn-outlined w-full sm:w-auto justify-center disabled:opacity-50"
          >
            {downloadingHistory ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download history
          </button>
        </div>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <div
          className="rounded-2xl p-3 border border-blue-200"
          style={{ background: 'linear-gradient(to right, #eff6ff, #dbeafe)' }}
        >
          <div className="text-[#0B4778] text-xs font-bold uppercase tracking-wider" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>
            Outstanding balance
          </div>
          <div className="flex items-end justify-between mt-1 gap-2 min-h-[2rem]">
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-[#0B4778]" aria-label="Loading outstanding balance" />
            ) : (
              <>
                <div className="text-2xl font-bold leading-tight text-[#0B4778]">${formatMoney(metrics.outstandingTotal)}</div>
                <div className="text-blue-800/80 text-xs font-medium">
                  Across {metrics.outstandingCount} invoice{metrics.outstandingCount === 1 ? '' : 's'}
                </div>
              </>
            )}
          </div>
        </div>
        <div
          className="rounded-2xl p-3 border border-gray-200"
          style={{ background: 'linear-gradient(to right, #f9fafb, #f3f4f6)' }}
        >
          <div className="text-gray-600 text-xs font-bold uppercase tracking-wider" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>
            Due soon
          </div>
          <div className="flex items-end justify-between mt-1 gap-2 min-h-[2rem]">
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-gray-600" aria-label="Loading due soon" />
            ) : (
              <>
                <div className="text-2xl font-bold leading-tight text-gray-900">${formatMoney(metrics.dueSoonTotal)}</div>
                <div className="text-gray-600 text-xs font-medium">
                  Due within 7 days, incl. today
                </div>
              </>
            )}
          </div>
        </div>
        <div
          className="rounded-2xl p-3 border border-orange-200"
          style={{ background: 'linear-gradient(to right, #fff7ed, #ffedd5)' }}
        >
          <div className="text-orange-700 text-xs font-bold uppercase tracking-wider" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>
            Past due
          </div>
          <div className="flex items-end justify-between mt-1 gap-2 min-h-[2rem]">
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-orange-700" aria-label="Loading past due" />
            ) : (
              <>
                <div className="text-2xl font-bold leading-tight text-orange-950">${formatMoney(metrics.pastDueTotal)}</div>
                <div className="text-orange-800/80 text-xs font-medium">
                  {metrics.pastDueCount} invoice{metrics.pastDueCount === 1 ? '' : 's'} overdue
                </div>
              </>
            )}
          </div>
        </div>
        <div
          className="rounded-2xl p-3 border border-green-200"
          style={{ background: 'linear-gradient(to right, #f0fdf4, #dcfce7)' }}
        >
          <div className="text-green-700 text-xs font-bold uppercase tracking-wider" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>
            Paid last 30 days
          </div>
          <div className="flex items-end justify-between mt-1 gap-2 min-h-[2rem]">
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-green-700" aria-label="Loading paid last 30 days" />
            ) : (
              <>
                <div className="text-2xl font-bold leading-tight text-green-950">${formatMoney(metrics.paidLast30Total)}</div>
                <div className="text-green-800/80 text-xs font-medium">
                  {metrics.paidLast30Count} invoice{metrics.paidLast30Count === 1 ? '' : 's'}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-2xl p-3">
        <div className="flex flex-col lg:flex-row gap-2 lg:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              className="aacore-textfield-primary pl-10"
              placeholder="Search invoice #, business, service, notes..."
            />
          </div>

          <div className="grid grid-cols-2 lg:flex gap-2">
            <div className="relative">
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as any);
                  setOffset(0);
                }}
                className="aacore-textfield-primary appearance-none pr-10"
                aria-label="Filter by status"
              >
                <option value="ALL">All Status</option>
                <option value="SENT">Unpaid</option>
                <option value="PAID">Paid</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={businessId}
                onChange={(e) => {
                  setBusinessId(e.target.value as any);
                  setOffset(0);
                }}
                className="aacore-textfield-primary appearance-none pr-10"
                aria-label="Filter by business"
              >
                <option value="ALL">All Businesses</option>
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div className="hidden md:flex gap-2 lg:justify-end">
            <button type="button" className={pillClass(status === 'ALL')} onClick={() => setStatus('ALL')}>
              All
            </button>
            <button type="button" className={pillClass(status === 'SENT')} onClick={() => setStatus('SENT')}>
              Unpaid
            </button>
            <button type="button" className={pillClass(status === 'PAID')} onClick={() => setStatus('PAID')}>
              Paid
            </button>
          </div>
        </div>
      </div>

      {/* Invoice list */}
      {loading ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <div className="text-gray-600 text-sm">Loading invoices…</div>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50 text-blue-700 flex-shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <div className="text-gray-900 font-semibold">No invoices to show</div>
              <div className="text-gray-600 text-sm mt-1">
                When a business sends you an invoice, you’ll be able to view and download it here.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filteredRows.map((r) => {
            const b = statusBadge(r.status);
            const isExpanded = expandedId === r.id;
            const isUnpaid = String(r.status || '').toUpperCase() === 'SENT';
            const ageDays = Math.max(0, daysBetween(r.invoice_date || r.created_at, new Date()));
            const items = parseServices(r.services).filter((li) => li.amount > 0 || li.qty > 0);
            const businessName = getBusinessName(r);
            const businessShortName = getBusinessShortName(r);
            const addressLine = getBusinessAddressLine(r);
            const businessEmail = getBusinessEmail(r);
            const businessPhone = getBusinessPhone(r);
            const serviceStart = getServiceStartDateTime(r.services);
            const serviceDateTimeText = serviceStart ? fmtDateTime(serviceStart) : '—';

            return (
              <div key={r.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3 relative">
                  <div className="flex gap-3 min-w-0">
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center border flex-shrink-0 ${
                        isUnpaid ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-green-50 text-green-700 border-green-200'
                      }`}
                    >
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 pr-16 md:pr-0">
                      <span
                        className={`md:hidden absolute top-4 right-4 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${b.className}`}
                      >
                        {b.label}
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-bold text-gray-900 truncate">{toInvoiceLabel(r.id)}</h3>
                        <span className={`hidden md:inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${b.className}`}>
                          {b.label}
                          {isUnpaid && ageDays > 0 ? ` · ${ageDays} days` : ''}
                        </span>
                        {r.appointment_id ? (
                          <Link
                            to={`/dashboard/customer/appointments/${r.appointment_id}`}
                            state={{ from: '/dashboard/customer/invoices' }}
                            className="hidden md:inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 transition-colors"
                            title="View appointment details"
                          >
                            From appointment
                          </Link>
                        ) : null}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
                        {businessShortName ? (
                          <Link
                            to={`/${businessShortName}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm font-medium hover:underline transition-colors"
                            style={{ color: '#0B4778' }}
                            title={`View ${businessName}`}
                          >
                            {businessName}
                          </Link>
                        ) : (
                          <span className="font-semibold text-gray-900">{businessName}</span>
                        )}
                        <span className="text-gray-300">•</span>
                        <span>Sent: {fmtDate(r.invoice_date)}</span>
                        <span className="text-gray-300">•</span>
                        <span>Due: {fmtDate(r.due_date)}</span>
                        <span className="text-gray-300">•</span>
                        {safeNum(r.deposit) > 0 ? (
                          <>
                            <span className="text-gray-600">Total: ${formatMoney(r.total)}</span>
                            <span className="text-gray-300">·</span>
                            <span className="font-bold text-gray-900">Amount due: ${formatMoney(getInvoiceAmountDue(r))}</span>
                          </>
                        ) : (
                          <span className="font-bold text-gray-900">${formatMoney(r.total)}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-stretch md:flex-row md:items-center md:justify-end gap-2">
                    {isUnpaid ? (
                      <button
                        type="button"
                        className="aacore-btn-primary justify-center w-full md:w-auto"
                        disabled={r.onlinePaymentsEnabled !== true}
                        title={
                          r.onlinePaymentsEnabled !== true
                            ? 'Online payments are not enabled for this business. See the invoice note for payment instructions.'
                            : undefined
                        }
                        onClick={() => {
                          if (r.onlinePaymentsEnabled !== true) return;
                          navigate(`/dashboard/customer/invoices/${r.id}/pay`);
                        }}
                      >
                        <CreditCard className="w-4 h-4" />
                        Pay now
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="aacore-btn-outlined justify-center w-full md:w-auto"
                        onClick={() => handleDownloadPdf(r.id)}
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    )}
                    <button
                      type="button"
                      className="aacore-btn-outlined justify-center w-full md:w-auto"
                      onClick={() => setExpandedId((prev) => (prev === r.id ? null : r.id))}
                    >
                      View details
                      <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                </div>

                {isUnpaid && r.onlinePaymentsEnabled !== true ? (
                  <div className="px-4 pb-4 pt-0">
                    <div
                      role="alert"
                      className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-amber-900"
                    >
                      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                      </div>
                      <div className="min-w-0 text-xs leading-relaxed text-amber-900">
                        <p className="text-sm font-semibold text-amber-950">Online payments are not enabled</p>
                        <p className="mt-1">
                          {businessName} has not set up online payments through AACore Booking. You will not be able to pay this
                          invoice online through the platform.
                        </p>
                        <p className="mt-2">Please see the note on this invoice for payment instructions from the business.</p>
                      </div>
                    </div>
                  </div>
                ) : null}

                {isExpanded && (
                  <div className="px-4 pb-4">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                      <div className="lg:col-span-5 bg-white border border-gray-200 rounded-2xl p-4">
                        <div className="space-y-3">
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">BUSINESS</div>
                            {businessShortName ? (
                              <Link
                                to={`/${businessShortName}`}
                                className="text-sm font-medium hover:underline transition-colors"
                                style={{ color: '#0B4778' }}
                                title={`View ${businessName}`}
                              >
                                {businessName}
                              </Link>
                            ) : (
                              <div className="text-sm text-gray-900 font-medium">{businessName}</div>
                            )}
                            {addressLine ? <div className="text-sm text-gray-600">{addressLine}</div> : null}
                            {businessEmail ? <div className="text-sm text-gray-600">{businessEmail}</div> : null}
                            {businessPhone ? <div className="text-sm text-gray-600">{businessPhone}</div> : null}
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">BILLED TO</div>
                            <div className="text-sm text-gray-900 font-medium">{String(r.bill_to || '').trim() || '—'}</div>
                            {r.customer_email ? <div className="text-sm text-gray-600">{String(r.customer_email).trim()}</div> : null}
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">SERVICE DATE</div>
                            {r.appointment_id ? (
                              <div>
                                <Link
                                  to={`/dashboard/customer/appointments/${r.appointment_id}`}
                                  state={{ from: '/dashboard/customer/invoices' }}
                                  className="text-sm font-medium hover:underline transition-colors"
                                  style={{ color: '#0B4778' }}
                                >
                                  {serviceDateTimeText}
                                </Link>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-900 font-medium">{serviceDateTimeText}</div>
                            )}
                          </div>
                          {r.notes ? (
                            <div>
                              <div className="text-xs text-gray-500 mb-0.5">Note</div>
                              <div className="text-sm text-gray-900 whitespace-pre-line">{String(r.notes).trim()}</div>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="lg:col-span-7 bg-white border border-gray-200 rounded-2xl p-4">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="text-sm font-semibold text-gray-900">Invoice items</div>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            <thead>
                              <tr className="text-left text-xs text-gray-500">
                                <th className="py-2 font-semibold">Item</th>
                                <th className="py-2 text-right w-20 font-semibold">Qty</th>
                                <th className="py-2 text-right w-28 font-semibold">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {items.length === 0 ? (
                                <tr>
                                  <td className="py-3 text-sm text-gray-600" colSpan={3}>
                                    No line items
                                  </td>
                                </tr>
                              ) : (
                                items.map((it, idx) => (
                                  <tr key={idx}>
                                    <td className="py-3">
                                      <div className="text-sm font-medium text-gray-900">{it.description}</div>
                                      {it.professionalName ? <div className="text-xs text-gray-500 mt-0.5">{it.professionalName}</div> : null}
                                    </td>
                                    <td className="py-3 text-right text-sm text-gray-600">{it.qty}</td>
                                    <td className="py-3 text-right text-sm font-semibold text-gray-900">${formatMoney(it.amount)}</td>
                                  </tr>
                                ))
                              )}
                              <tr>
                                <td className="py-2 text-right text-sm text-gray-600" colSpan={2}>
                                  Subtotal
                                </td>
                                <td className="py-2 text-right text-sm font-semibold text-gray-900">${formatMoney(r.sub_total)}</td>
                              </tr>
                              {safeNum(r.discount) > 0 && (
                                <tr>
                                  <td className="py-2 text-right text-sm text-gray-600" colSpan={2}>
                                    Discount
                                  </td>
                                  <td className="py-2 text-right text-sm font-semibold text-gray-900">-${formatMoney(r.discount)}</td>
                                </tr>
                              )}
                              <tr>
                                <td className="py-2 text-right text-sm text-gray-600" colSpan={2}>
                                  Tax
                                </td>
                                <td className="py-2 text-right text-sm font-semibold text-gray-900">${formatMoney(r.tax)}</td>
                              </tr>
                              <tr>
                                <td className="py-2 text-right text-sm text-gray-600" colSpan={2}>
                                  Tip
                                </td>
                                <td className="py-2 text-right text-sm font-semibold text-gray-900">${formatMoney(r.tip)}</td>
                              </tr>
                              <tr>
                                <td className="py-3 text-right text-sm font-semibold text-gray-900" colSpan={2}>
                                  Total
                                </td>
                                <td className="py-3 text-right text-sm font-semibold text-gray-900">${formatMoney(r.total)}</td>
                              </tr>
                              {safeNum(r.deposit) > 0 && (
                                <tr>
                                  <td className="py-2 text-right text-sm text-gray-600" colSpan={2}>
                                    Deposit (already paid)
                                  </td>
                                  <td className="py-2 text-right text-sm font-semibold text-gray-900">-${formatMoney(r.deposit)}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div className="mt-3 rounded-lg px-4 py-3 bg-[#0B4778]/10 border border-[#0B4778]/20">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-[#0B4778]">Amount due</span>
                            <span className="text-lg font-bold text-[#0B4778]">
                              ${formatMoney(getInvoiceAmountDue(r))}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 mt-3">
                          <button type="button" className="aacore-btn-outlined w-full justify-center" onClick={() => handleDownloadPdf(r.id)}>
                            <Download className="w-4 h-4" />
                            Download PDF
                          </button>
                          <button type="button" className="aacore-btn-outlined w-full justify-center" onClick={() => setReportInfoOpen(true)}>
                            <Flag className="w-4 h-4" />
                            Report an issue
                          </button>
                        </div>

                        <div className="mt-3 p-3 border border-gray-200 rounded-xl bg-gray-50 text-sm text-gray-600 flex gap-2">
                          <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                          <div>
                            AACore Booking is not a point-of-sale system. Invoices appear here only when a business sends you an invoice for payment or follow-up after a service, quote, or appointment.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination */}
          {paginationTotal > limit && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-white border border-gray-200 rounded-2xl p-3">
              <div className="text-sm text-gray-600">
                Page <span className="font-semibold text-gray-900">{pageInfo.currentPage}</span> of{' '}
                <span className="font-semibold text-gray-900">{pageInfo.totalPages}</span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="aacore-btn-outlined"
                  disabled={offset <= 0}
                  onClick={() => setOffset((o) => Math.max(0, o - limit))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="aacore-btn-outlined"
                  disabled={offset + limit >= paginationTotal}
                  onClick={() => setOffset((o) => o + limit)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="text-gray-600 text-sm">
        Tip: If you paid in person, the business may later mark your invoice as paid for record-keeping. If an invoice looks incorrect, use{' '}
        <span className="font-semibold text-gray-900">Report an issue</span>.
      </div>

      <CustomAlertModal
        isOpen={howOpen}
        onClose={() => setHowOpen(false)}
        title="How invoices work"
        message=""
        type="info"
        content={
          <div className="space-y-3 text-gray-700">
            <p className="text-sm text-gray-600">
              AACore Booking supports billing when a business needs to charge after service.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
              <div className="font-semibold text-gray-900 mb-2">Key points</div>
              <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                <li>
                  <span className="font-semibold text-gray-900">Not every appointment has an invoice.</span> Many businesses accept payment in-store
                  and don’t send invoices.
                </li>
                <li>Invoices appear here only when a business sends you one to pay later.</li>
                <li>Some businesses may allow online payment; others will mark invoices paid after in-person payment.</li>
                <li>If something looks wrong, use <span className="font-semibold text-gray-900">Report an issue</span>.</li>
              </ul>
            </div>
          </div>
        }
        confirmText="Close"
      />

      <CustomAlertModal
        isOpen={reportInfoOpen}
        onClose={() => setReportInfoOpen(false)}
        title="Report an issue"
        message="Issue reporting/dispute workflow is coming soon. For now, please contact the business directly and keep the invoice PDF for reference."
        type="info"
        confirmText="OK"
      />
    </div>
  );
};

export default CustomerInvoicesSection;

